import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IPersonDTO } from '@modeso/types__aumico-lib-companies-be';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import * as fromActions from '../actions/persons.actions';
import { AumicoPersonsService } from '../services/persons.service';

/** REV-22155 — start action → service (HTTP) → success / failure action. */
@Injectable()
export class PersonsEffects {
    constructor(private actions$: Actions, private service: AumicoPersonsService) {}

    getPersons$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getPersons),
            mergeMap(() =>
                this.service.getPersons().pipe(
                    map((persons: IPersonDTO[]) => fromActions.onGettingPersonsSuccessfully({ payload: persons })),
                    catchError((error: HttpErrorResponse) => of(fromActions.onGettingPersonsFailed({ payload: error }))),
                ),
            ),
        ),
    );

    getPersonsOfCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getPersonsOfCompany),
            mergeMap(({ companyId }) =>
                this.service.getPersons(companyId).pipe(
                    map((persons: IPersonDTO[]) =>
                        fromActions.onGettingPersonsOfCompanySuccessfully({ payload: persons }),
                    ),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.onGettingPersonsOfCompanyFailed({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    getPersonById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getPersonById),
            mergeMap(({ id }) =>
                this.service.getPersonById(id).pipe(
                    map((person: IPersonDTO) => fromActions.onGettingPersonByIdSuccessfully({ payload: person })),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.onGettingPersonByIdFailed({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    createPerson$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.createPerson),
            mergeMap(({ person }) =>
                this.service.createPerson(person).pipe(
                    map((created: IPersonDTO) => fromActions.onCreatePersonSuccessfully({ payload: created })),
                    catchError((error: HttpErrorResponse) => of(fromActions.onCreatePersonFailed({ payload: error }))),
                ),
            ),
        ),
    );

    updatePerson$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updatePerson),
            mergeMap(({ person, tasksUpdate }) =>
                this.service.updatePerson(person, tasksUpdate).pipe(
                    map((updated: IPersonDTO) => fromActions.onUpdatePersonSuccessfully({ payload: updated })),
                    catchError((error: HttpErrorResponse) => of(fromActions.onUpdatePersonFailed({ payload: error }))),
                ),
            ),
        ),
    );

    changePersonStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.changePersonStatus),
            mergeMap(({ id, active }) =>
                this.service.updatePersonStatus(id, active).pipe(
                    map((updated: IPersonDTO) => fromActions.onChangePersonStatusSuccessfully({ payload: updated })),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.onChangePersonStatusFailed({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    deletePerson$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.deletePerson),
            mergeMap(({ id }) =>
                this.service.deletePerson(id).pipe(
                    // 204 without a body — the id comes from the action.
                    map(() => fromActions.onDeletePersonSuccessfully({ id })),
                    catchError((error: HttpErrorResponse) => of(fromActions.onDeletePersonFailed({ payload: error }))),
                ),
            ),
        ),
    );

    bulkUpdatePersons$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.bulkUpdatePersons),
            mergeMap(({ persons, tasksUpdate }) =>
                this.service.bulkUpdatePersons(persons, tasksUpdate).pipe(
                    map((updated: IPersonDTO[]) => fromActions.onBulkUpdatePersonsSuccessfully({ payload: updated })),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.onBulkUpdatePersonsFailed({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    /** Like the companies export: the blob is saved under the file name the page built. */
    exportPersonsDataAsExcel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.exportPersonsDataAsExcel.do),
            mergeMap(({ payload }) =>
                this.service.exportPersonsDataAsExcel$({ tableHeaders: payload.tableHeaders, persons: payload.persons }).pipe(
                    map((response) => {
                        const blob = new Blob([response], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        });
                        const downloadLink = document.createElement('a');
                        downloadLink.href = window.URL.createObjectURL(blob);
                        downloadLink.download = payload.fileName;
                        downloadLink.click();
                        window.URL.revokeObjectURL(downloadLink.href);
                        return fromActions.exportPersonsDataAsExcel.success();
                    }),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.exportPersonsDataAsExcel.failed({ payload: error })),
                    ),
                ),
            ),
        ),
    );
}
